import {PercentPipe} from '@angular/common'
import {Component, effect, inject, OnInit} from '@angular/core'
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms'
import {
  MatError,
  MatFormField,
  MatHint,
  MatLabel,
  MatSuffix
} from '@angular/material/form-field'
import {MatIcon} from '@angular/material/icon'
import {MatInput} from '@angular/material/input'
import {
  FormatNumberDirective,
  getTakeUntilDestroyed
} from '@sparbanken-syd/sparbanken-syd-theme'
import {Subject} from 'rxjs'
import {debounceTime, distinctUntilChanged, switchMap} from 'rxjs/operators'
import {DataService} from '../../services/data.service'
import {HemnetService, IHemnetData} from '../../services/hemnet.service'

export const minDownPaymentValidator = (expectedPrice: number, currentDownPaymentPercent: number): ValidatorFn =>
  (control: AbstractControl): ValidationErrors | null => {
    const downPayment = Number.parseInt(DataService.RemoveNonDigitsFromString(control.value), 10)

    const minRequired = Math.ceil(expectedPrice * currentDownPaymentPercent)

    return downPayment < minRequired
      ? {minDownPayment: {required: minRequired, actual: downPayment}}
      : null
  }

/** Check some basic inputs before returning */
export const inputValidator: ValidationErrors = (control: FormGroup): ValidationErrors | null => {
  const expectedPrice = control.get('expectedPrice')
  const downPayment = control.get('downPayment')
  const interest = control.get('interestRate')
  const price = Number.parseInt(DataService.RemoveNonDigitsFromString(expectedPrice!.value), 10)
  const down = Number.parseInt(DataService.RemoveNonDigitsFromString(downPayment!.value), 10)

  if (down >= price) {
    return {tooLarge: true}
  }

  const rate = Number.parseFloat(interest!.value)
  if (isNaN(rate)) {
    return {notNumber: true}
  }
  if (rate > 20) {
    return {tooHigh: true}
  }
  return null
}

@Component({
  selector: 'spb-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  imports: [ReactiveFormsModule, MatHint, MatError, FormatNumberDirective, MatFormField, MatInput, MatLabel, MatSuffix, MatIcon, PercentPipe]
})
export class InputComponent implements OnInit {
  public DELAY = 3000
  public inputForm: FormGroup = new FormGroup({
    expectedPrice: new FormControl<number>(0, [Validators.required, Validators.min(1), Validators.max(99999999)]),
    downPayment: new FormControl<number>(0, [Validators.required]),
    interestRate: new FormControl<string | null>(null, [Validators.required]),
    fee: new FormControl<number>(0, [Validators.max(99999)]),
    runCost: new FormControl<number>(0, [Validators.max(999999)])
  }, inputValidator)

  public calculatedDownPayment = ''
  public listRate?: string = undefined
  public readonly hemnetService = inject(HemnetService)


  private hemnetData$ = new Subject<IHemnetData>()
  private hemnetData: IHemnetData = {} as any
  private readonly takeUntilDestroyed = getTakeUntilDestroyed()

  /**
   * Need this to update form when test mode changes. Could be removed after 2026-04-01
   */
  constructor() {
    effect(() => {
      const rate = this.hemnetService.currentDownPaymentPercent()
      const rawPrice = this.inputForm.get('expectedPrice')?.value
      const price = parseInt(DataService.RemoveNonDigitsFromString(rawPrice), 10)
      this.updateDownPaymentValidator(price)
      this.inputForm.patchValue(
        {downPayment: Math.ceil(price * rate)},
        {emitEvent: false}
      )
    })
  }


  public ngOnInit(): void {
    this.hemnetData$ = this.hemnetService.hemnetData
    this.hemnetService.hemnetOutput.pipe(this.takeUntilDestroyed())
      .subscribe({
        next: hemnetData => {
          if (!hemnetData.expectedPrice) {
            hemnetData.expectedPrice = hemnetData.askingPrice
          }

          if (!hemnetData.downPayment) {
            hemnetData.downPayment = Math
              .ceil(hemnetData.expectedPrice * this.hemnetService.currentDownPaymentPercent())
          }

          this.hemnetData = hemnetData

          const formattedRate = hemnetData.interestRate?.toLocaleString('fr', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })

          this.listRate = formattedRate

          // Set the initial form with formatted interest rate
          this.inputForm.patchValue({
            ...hemnetData,
            interestRate: formattedRate
          })

          // Update validators
          this.updateDownPaymentValidator(hemnetData.expectedPrice)

          // Send the data to listeners.
          this.hemnetData$.next(this.hemnetData)
          this.hemnetService.saveHemnetData(this.hemnetData)
        }
      })

    // Listen for expected price changes to update validator
    this.inputForm.get('expectedPrice')!.valueChanges.pipe(
      this.takeUntilDestroyed(),
      distinctUntilChanged()
    ).subscribe(value => {
      const cleanValue = Number.parseInt(DataService.RemoveNonDigitsFromString(value), 10)
      this.updateDownPaymentValidator(cleanValue)
      this.hemnetData.expectedPrice = cleanValue
    })

    // Do all form changes
    this.inputForm.valueChanges.pipe(this.takeUntilDestroyed())
      .subscribe(formData => {
        if (this.inputForm.valid) {
          Object.assign(this.hemnetData, formData)
          // Convert interestRate string to number for calculations
          this.hemnetData.interestRate = parseFloat(formData.interestRate.replace(',', '.'))
          this.hemnetData$.next(this.hemnetData)
          this.hemnetService.saveHemnetData(this.hemnetData)
        }
      })

    /**
     * This is serious over engineering! We could just as well save before we
     * navigate out, but I wanted to test this debouncce/switchmap thing.
     *
     * When the form is valid it emits, if we emit more than every three seconds
     * we simply skip those values and save the latest... hmm.
     */
    this.inputForm.valueChanges.pipe(
      debounceTime(this.DELAY),
      switchMap(() => this.hemnetService.saveHemnetData(this.hemnetData))
    ).subscribe((hemnetData: IHemnetData) => {
      HemnetService.CreateCookie(hemnetData)
    })
  }

  private updateDownPaymentValidator(expectedPrice: number): void {
    // Update validator on down payment field
    this.inputForm.get('downPayment')!.setValidators([
      Validators.required,
      Validators.max(expectedPrice),
      minDownPaymentValidator(expectedPrice, this.hemnetService.currentDownPaymentPercent())
    ])

    // Update value and validity WITHOUT triggering events
    this.inputForm.get('downPayment')!.updateValueAndValidity({emitEvent: false})

    // Update displayed value
    this.calculatedDownPayment = DataService
      .AddNumberGrouping(expectedPrice * this.hemnetService.currentDownPaymentPercent())
  }
}